import React, { useRef, useState, useEffect } from 'react';

const LetterCanvas = ({ image, strokes, onComplete, onResetRef }) => {
    const canvasRef = useRef(null);
    const [drawing, setDrawing] = useState(false);
    const currentStroke = useRef([]);
    const [message, setMessage] = useState('');
    const [completedStrokes, setCompletedStrokes] = useState(new Set());
    const [showHint, setShowHint] = useState(true);
    const [hasStartedDrawing, setHasStartedDrawing] = useState(false);
    const [allDrawnStrokes, setAllDrawnStrokes] = useState([]);
    const [isChecking, setIsChecking] = useState(false);
    const checkTimeoutRef = useRef(null);

    const resetCanvas = () => {
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        setCompletedStrokes(new Set());
        setMessage('');
        setShowHint(true);
        setHasStartedDrawing(false);
        setAllDrawnStrokes([]);
        setIsChecking(false);
        if (checkTimeoutRef.current) {
            clearTimeout(checkTimeoutRef.current);
        }
    };

    // Expose reset function to parent
    useEffect(() => {
        if (onResetRef) {
            onResetRef(resetCanvas);
        }
    }, [onResetRef]);

    // Show animated hint when component mounts or resets
    useEffect(() => {
        if (showHint && canvasRef.current) {
            const ctx = canvasRef.current.getContext('2d');
            let isCancelled = false;

            const animateAllStrokes = () => {
                let currentStrokeIndex = 0;

                const animateNextStroke = () => {
                    if (isCancelled || !showHint || hasStartedDrawing) return;

                    if (currentStrokeIndex >= strokes.length) {
                        // All strokes drawn, wait then loop
                        setTimeout(() => {
                            if (!isCancelled && showHint && !hasStartedDrawing) {
                                ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                                animateAllStrokes();
                            }
                        }, 1500);
                        return;
                    }

                    const stroke = strokes[currentStrokeIndex];
                    const interpolatedPoints = generateInterpolatedStroke(stroke);
                    let pointIndex = 0;

                    const drawStrokeAnimation = () => {
                        if (isCancelled || !showHint || hasStartedDrawing) return;

                        if (pointIndex < interpolatedPoints.length - 1) {
                            ctx.beginPath();
                            ctx.moveTo(interpolatedPoints[pointIndex].x, interpolatedPoints[pointIndex].y);
                            ctx.lineTo(interpolatedPoints[pointIndex + 1].x, interpolatedPoints[pointIndex + 1].y);
                            ctx.strokeStyle = 'rgba(0, 150, 255, 0.6)';
                            ctx.lineWidth = 5;
                            ctx.lineCap = 'round';
                            ctx.lineJoin = 'round';
                            ctx.stroke();
                            pointIndex++;
                            requestAnimationFrame(drawStrokeAnimation);
                        } else {
                            // This stroke is complete, move to next stroke
                            currentStrokeIndex++;
                            setTimeout(() => {
                                if (!isCancelled && showHint && !hasStartedDrawing) {
                                    animateNextStroke();
                                }
                            }, 400);
                        }
                    };

                    drawStrokeAnimation();
                };

                animateNextStroke();
            };

            animateAllStrokes();

            return () => {
                isCancelled = true;
            };
        }
    }, [showHint, strokes, hasStartedDrawing]);

    const generateInterpolatedStroke = ({ start, end }) => {
        const points = [];
        const steps = 20;
        for (let i = 0; i <= steps; i++) {
            points.push({
                x: start.x + (end.x - start.x) * (i / steps),
                y: start.y + (end.y - start.y) * (i / steps),
            });
        }
        return points;
    };

    const getCanvasPos = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        const clientX = e.clientX || e.touches?.[0]?.clientX;
        const clientY = e.clientY || e.touches?.[0]?.clientY;
        return { x: clientX - rect.left, y: clientY - rect.top };
    };

    const startDrawing = (e) => {
        e.preventDefault();
        
        // Hide hint when user starts drawing
        if (showHint) {
            setShowHint(false);
            setHasStartedDrawing(true);
            const ctx = canvasRef.current.getContext('2d');
            ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        }
        
        setDrawing(true);
        currentStroke.current = [getCanvasPos(e)];
    };

    const draw = (e) => {
        if (!drawing) return;
        const ctx = canvasRef.current.getContext('2d');
        const pos = getCanvasPos(e);
        const last = currentStroke.current[currentStroke.current.length - 1];

        ctx.beginPath();
        ctx.moveTo(last.x, last.y);
        ctx.lineTo(pos.x, pos.y);
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 5;
        ctx.stroke();

        currentStroke.current.push(pos);
    };

    const endDrawing = () => {
        if (!drawing) return;
        setDrawing(false);
        const stroke = [...currentStroke.current];
        
        // Store all drawn strokes
        setAllDrawnStrokes(prev => [...prev, stroke]);
        
        // Clear any existing timeout
        if (checkTimeoutRef.current) {
            clearTimeout(checkTimeoutRef.current);
        }
        
        // Wait 2 seconds after last stroke to check the complete drawing
        checkTimeoutRef.current = setTimeout(() => {
            checkCompleteDrawing();
        }, 2000);
    };

    const checkCompleteDrawing = () => {
        if (isChecking || allDrawnStrokes.length === 0) return;
        
        setIsChecking(true);
        setMessage('Checking your drawing...');
        
        // Flatten all drawn points into a single array for analysis
        const allDrawnPoints = allDrawnStrokes.flat();
        
        // Check shape characteristics
        const isDrawnShapeClosed = checkIfShapeClosed(allDrawnPoints);
        const isExpectedShapeClosed = checkIfExpectedShapeClosed(strokes);
        
        // FIXED: Critical validation - reject if shape open/closed doesn't match
        // For example: C is open, O is closed - they should not match
        if (isDrawnShapeClosed !== isExpectedShapeClosed) {
            setMessage('❌ Try again! Draw the letter shown above.');
            
            const utterance = new SpeechSynthesisUtterance('Try again');
            utterance.rate = 0.9;
            utterance.volume = 1;
            speechSynthesis.speak(utterance);
            
            const wrongSound = new Audio('/sounds/wrong.mp3');
            wrongSound.volume = 1;
            wrongSound.play().catch(err => console.error('Error playing wrong sound:', err));
            
            setIsChecking(false);
            return;
        }
        
        // Check if all expected strokes are covered by the drawn strokes
        const matchedStrokes = new Set();
        
        for (let i = 0; i < strokes.length; i++) {
            const expected = strokes[i];
            let matched = false;
            
            // Check each drawn stroke against this expected stroke
            for (const drawnStroke of allDrawnStrokes) {
                if (drawnStroke.length < 2) continue;
                
                // Check if drawn stroke covers the expected stroke area
                if (strokeCoversArea(drawnStroke, expected)) {
                    matched = true;
                    break;
                }
            }
            
            if (matched) {
                matchedStrokes.add(i);
            }
        }
        
        // Check if all strokes are matched (with some tolerance)
        const matchPercentage = (matchedStrokes.size / strokes.length) * 100;
        
        // FIXED: Adjust validation based on letter complexity - more lenient for complex letters
        // For complex letters (more strokes), require lower percentage
        let requiredPercentage = 70;
        if (strokes.length >= 10) {
            requiredPercentage = 50; // 50% for very complex letters (like G with 12 strokes)
        } else if (strokes.length >= 7) {
            requiredPercentage = 60; // 60% for moderately complex letters
        }
        
        const minRequiredStrokes = Math.max(1, Math.ceil(strokes.length * (requiredPercentage / 100)));
        
        // Check if drawing meets requirements
        if (matchPercentage >= requiredPercentage && matchedStrokes.size >= minRequiredStrokes) {
            // Keep user's drawing visible and show success message
            setMessage('🎉 Correct! Perfect letter!');
            setCompletedStrokes(matchedStrokes);
            
            // Speak "Excellent" 
            const utterance = new SpeechSynthesisUtterance('Excellent');
            utterance.rate = 0.9;
            utterance.volume = 1;
            speechSynthesis.speak(utterance);
            
            // Draw correct strokes in green over user's drawing after a brief moment
            setTimeout(() => {
                overlayCorrectStrokes();
                // Wait a bit to show the correct overlay, then complete
                setTimeout(() => {
                    onComplete?.();
                }, 1500);
            }, 800);
        } else {
            // FIXED: When drawing is wrong, show error message, speak "try again", and play wrong sound
            setMessage('❌ Try again! Draw the letter shown above.');
            
            // Speak "try again"
            const utterance = new SpeechSynthesisUtterance('Try again');
            utterance.rate = 0.9;
            utterance.volume = 1;
            speechSynthesis.speak(utterance);
            
            // Play wrong sound
            const wrongSound = new Audio('/sounds/wrong.mp3');
            wrongSound.volume = 1;
            wrongSound.play().catch(err => console.error('Error playing wrong sound:', err));
            
            // FIXED: Automatically clear and retry after 2 seconds
            setTimeout(() => {
                const ctx = canvasRef.current.getContext('2d');
                ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                setAllDrawnStrokes([]);
                setMessage('');
                setIsChecking(false);
                setShowHint(true);
                setHasStartedDrawing(false);
            }, 2000);
        }
    };

    const checkIfShapeClosed = (points) => {
        if (points.length < 10) return false;
        
        // Check if the drawing forms a closed loop by comparing first and last points
        const firstPoint = points[0];
        const lastPoint = points[points.length - 1];
        const distance = Math.hypot(lastPoint.x - firstPoint.x, lastPoint.y - firstPoint.y);
        
        // Also check if the drawing covers all four quadrants (indicating a closed shape)
        const bounds = {
            minX: Math.min(...points.map(p => p.x)),
            maxX: Math.max(...points.map(p => p.x)),
            minY: Math.min(...points.map(p => p.y)),
            maxY: Math.max(...points.map(p => p.y))
        };
        
        const centerX = (bounds.minX + bounds.maxX) / 2;
        const centerY = (bounds.minY + bounds.maxY) / 2;
        
        // Check coverage in all quadrants
        const hasTopLeft = points.some(p => p.x < centerX && p.y < centerY);
        const hasTopRight = points.some(p => p.x > centerX && p.y < centerY);
        const hasBottomLeft = points.some(p => p.x < centerX && p.y > centerY);
        const hasBottomRight = points.some(p => p.x > centerX && p.y > centerY);
        
        const quadrantCount = [hasTopLeft, hasTopRight, hasBottomLeft, hasBottomRight].filter(Boolean).length;
        
        // Shape is closed if endpoints are close AND it covers all 4 quadrants
        return distance < 50 && quadrantCount === 4;
    };
    
    const checkIfExpectedShapeClosed = (expectedStrokes) => {
        if (expectedStrokes.length === 0) return false;
        
        // Get all points from expected strokes
        const allPoints = [];
        expectedStrokes.forEach(stroke => {
            allPoints.push(stroke.start);
            allPoints.push(stroke.end);
        });
        
        const firstStroke = expectedStrokes[0];
        const lastStroke = expectedStrokes[expectedStrokes.length - 1];
        
        // Check if last stroke's end connects back to first stroke's start
        const distance = Math.hypot(
            lastStroke.end.x - firstStroke.start.x, 
            lastStroke.end.y - firstStroke.start.y
        );
        
        // Check quadrant coverage
        const bounds = {
            minX: Math.min(...allPoints.map(p => p.x)),
            maxX: Math.max(...allPoints.map(p => p.x)),
            minY: Math.min(...allPoints.map(p => p.y)),
            maxY: Math.max(...allPoints.map(p => p.y))
        };
        
        const centerX = (bounds.minX + bounds.maxX) / 2;
        const centerY = (bounds.minY + bounds.maxY) / 2;
        
        const hasTopLeft = allPoints.some(p => p.x < centerX && p.y < centerY);
        const hasTopRight = allPoints.some(p => p.x > centerX && p.y < centerY);
        const hasBottomLeft = allPoints.some(p => p.x < centerX && p.y > centerY);
        const hasBottomRight = allPoints.some(p => p.x > centerX && p.y > centerY);
        
        const quadrantCount = [hasTopLeft, hasTopRight, hasBottomLeft, hasBottomRight].filter(Boolean).length;
        
        // Shape is closed if endpoints connect AND covers all 4 quadrants
        return distance < 50 && quadrantCount === 4;
    };

    const strokeCoversArea = (drawnStroke, expectedStroke) => {
        const tolerance = 65; // FIXED: Balanced tolerance
        
        // Find points in drawn stroke that are near the expected stroke path
        let nearStartCount = 0;
        let nearEndCount = 0;
        let nearPathCount = 0;
        
        for (const point of drawnStroke) {
            // Check distance to start point
            const distToStart = Math.hypot(point.x - expectedStroke.start.x, point.y - expectedStroke.start.y);
            if (distToStart < tolerance) nearStartCount++;
            
            // Check distance to end point
            const distToEnd = Math.hypot(point.x - expectedStroke.end.x, point.y - expectedStroke.end.y);
            if (distToEnd < tolerance) nearEndCount++;
            
            // Check distance to the line between start and end
            const distToLine = pointToLineDistance(point, expectedStroke.start, expectedStroke.end);
            if (distToLine < tolerance) nearPathCount++;
        }
        
        // FIXED: More flexible requirements - especially for small/complex strokes
        const hasStart = nearStartCount >= 1; // At least 1 point near start
        const hasEnd = nearEndCount >= 1; // At least 1 point near end
        const hasPath = nearPathCount > drawnStroke.length * 0.25; // At least 25% of points along path
        
        return hasStart && hasEnd && hasPath;
    };

    const pointToLineDistance = (point, lineStart, lineEnd) => {
        const A = point.x - lineStart.x;
        const B = point.y - lineStart.y;
        const C = lineEnd.x - lineStart.x;
        const D = lineEnd.y - lineStart.y;
        
        const dot = A * C + B * D;
        const lenSq = C * C + D * D;
        
        if (lenSq === 0) return Math.hypot(A, B);
        
        let param = dot / lenSq;
        param = Math.max(0, Math.min(1, param));
        
        const xx = lineStart.x + param * C;
        const yy = lineStart.y + param * D;
        
        return Math.hypot(point.x - xx, point.y - yy);
    };


    const overlayCorrectStrokes = () => {
        // Draw correct strokes in green over the user's drawing (don't clear canvas)
        const ctx = canvasRef.current.getContext('2d');
        ctx.lineWidth = 6;
        ctx.strokeStyle = 'rgba(39, 174, 96, 0.7)'; // Semi-transparent green
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        // Draw all correct strokes
        strokes.forEach(stroke => {
            const points = generateInterpolatedStroke(stroke);
            ctx.beginPath();
            ctx.moveTo(points[0].x, points[0].y);
            for (let i = 1; i < points.length; i++) {
                ctx.lineTo(points[i].x, points[i].y);
            }
            ctx.stroke();
        });
    };



    return (
        <>
            <div style={{ position: 'relative', width: 300, height: 300 }}>
                <img src={image} alt="letter" style={{ width: '100%', height: '100%' }} />
                <canvas
                    ref={canvasRef}
                    width={300}
                    height={300}
                    style={{ position: 'absolute', top: 0, left: 0, cursor: 'crosshair' }}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={endDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={endDrawing}
                />
            </div>
            {message && (
                <div style={{ 
                    marginTop: 10, 
                    fontWeight: 'bold', 
                    fontSize: '16px',
                    color: message.includes('Perfect') || message.includes('Correct') ? '#27AE60' : 
                           message.includes('Keep') || message.includes('Checking') ? '#3498DB' : '#E74C3C' 
                }}>
                    {message}
                </div>
            )}
            {hasStartedDrawing && allDrawnStrokes.length > 0 && !message.includes('Perfect') && (
                <button
                    onClick={() => {
                        if (checkTimeoutRef.current) {
                            clearTimeout(checkTimeoutRef.current);
                        }
                        checkCompleteDrawing();
                    }}
                    style={{
                        marginTop: '12px',
                        padding: '10px 24px',
                        fontSize: '16px',
                        fontWeight: '600',
                        borderRadius: '10px',
                        border: 'none',
                        background: 'linear-gradient(135deg, #3498DB 0%, #2980B9 100%)',
                        color: '#fff',
                        cursor: 'pointer',
                        boxShadow: '0 4px 10px rgba(52, 152, 219, 0.3)',
                        transition: 'transform 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                    onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                >
                    ✓ Check My Drawing
                </button>
            )}
        </>
    );
};

export default LetterCanvas;
